import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Kpis, Panel, useFlash, readForm } from '../../ui/kit.js';
import { day, nextId } from '../../state/building.js';

import VendorElevator from '../../media/images/modules/vendor-elevator.jpg';
import VendorHvac from '../../media/images/modules/vendor-hvac.jpg';
import VendorPest from '../../media/images/modules/vendor-pest.jpg';
import VendorLandscape from '../../media/images/modules/vendor-landscape.jpg';
import VendorPool from '../../media/images/modules/vendor-pool.jpg';
import VendorFire from '../../media/images/modules/vendor-fire.jpg';

const SERVICES = ['Elevator', 'HVAC', 'Pest control', 'Landscaping', 'Pool service', 'Fire and life safety', 'Janitorial', 'Other'];

/* A photo per service type; new vendors pick theirs up from the form. */
const SERVICE_PHOTOS = {
    'Elevator': VendorElevator,
    'HVAC': VendorHvac,
    'Pest control': VendorPest,
    'Landscaping': VendorLandscape,
    'Pool service': VendorPool,
    'Fire and life safety': VendorFire
};
const photoOf = vendor => SERVICE_PHOTOS[vendor.service] || VendorHvac;

/* Contract end and COI expiry are day offsets from today. */
const SEED = [
    { id: 1, name: 'Atlantic Vertical Elevator', service: 'Elevator', contact: 'Ray Delgado', phone: '(305) 555-0142', contractEnd: 214, coi: 96, rating: 4.6 },
    { id: 2, name: 'Coastal Air Mechanical', service: 'HVAC', contact: 'Tanya Morales', phone: '(954) 555-0187', contractEnd: 45, coi: 18, rating: 4.8 },
    { id: 3, name: 'Keys Pest Solutions', service: 'Pest control', contact: 'Mike Ferris', phone: '(305) 555-0119', contractEnd: 120, coi: -6, rating: 4.2 },
    { id: 4, name: 'Brickell Green Landscaping', service: 'Landscaping', contact: 'Ana Suarez', phone: '(786) 555-0163', contractEnd: 300, coi: 240, rating: 4.4 },
    { id: 5, name: 'Blue Wave Pools', service: 'Pool service', contact: 'Kevin Otero', phone: '(305) 555-0175', contractEnd: 28, coi: 9, rating: 3.9 },
    { id: 6, name: 'Bayfront Fire & Life Safety', service: 'Fire and life safety', contact: 'Laura Chen', phone: '(954) 555-0131', contractEnd: 365, coi: 150, rating: 4.9 }
];

const coiChip = v => {
    if (v.requested) return { tone: 'info', text: 'Requested' };
    if (v.coi < 0) return { tone: 'alert', text: 'COI expired' };
    if (v.coi <= 30) return { tone: 'warn', text: 'COI expiring' };
    return { tone: 'ok', text: 'COI valid' };
};

/* Days from today to a date input's value (yyyy-mm-dd). */
const offsetOf = value => {
    if (!value) return 365;
    const [y, m, d] = value.split('-').map(Number);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((new Date(y, m - 1, d) - today) / 86400000);
};

/* Vendor Management: the building's service contracts, with insurance
   certificates tracked so no uninsured crew gets past the dock. */
function VendorsScreen() {
    const [vendors, setVendors] = useState(SEED);
    const [flash, notify] = useFlash();

    const requestCoi = vendor => {
        setVendors(list => list.map(v => (v.id === vendor.id ? { ...v, requested: true } : v)));
        notify(`COI request emailed to ${vendor.contact} at ${vendor.name}. Building named as additional insured.`);
    };

    const submit = event => {
        const values = readForm(event, ['name', 'contact']);
        if (!values) return notify('Add the company and a contact name first.');

        setVendors(list => [{
            id: nextId(),
            name: values.name,
            service: values.service,
            contact: values.contact,
            phone: values.phone,
            contractEnd: offsetOf(values.contractEnd),
            coi: offsetOf(values.coi),
            rating: 0
        }, ...list]);
        notify(`${values.name} added to the vendor directory.`);
    };

    const expired = vendors.filter(v => !v.requested && v.coi < 0).length;
    const expiring = vendors.filter(v => !v.requested && v.coi >= 0 && v.coi <= 30).length;

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Vendor management" title="Vendors and contracts" />

            <Kpis items={[
                { label: 'Active vendors', value: vendors.length, tone: 'info' },
                { label: 'COI expired', value: expired, tone: expired ? 'alert' : 'ok' },
                { label: 'COI expiring 30 days', value: expiring, tone: expiring ? 'warn' : 'ok' },
                { label: 'Renewals in 60 days', value: vendors.filter(v => v.contractEnd <= 60).length, tone: 'warn' }
            ]} />

            {flash}

            <div className="split">
                <Panel as="form" className="panel--accent" onSubmit={submit}>
                    <h4>Add vendor</h4>
                    <div className="form">
                        <Field label="Company" wide>
                            <input name="name" className="form-control" placeholder="e.g. Atlantic Window Cleaning" />
                        </Field>
                        <Field label="Service">
                            <select name="service" className="form-control" defaultValue="Other">
                                {SERVICES.map(s => <option key={s}>{s}</option>)}
                            </select>
                        </Field>
                        <Field label="Contact">
                            <input name="contact" className="form-control" placeholder="Full name" />
                        </Field>
                        <Field label="Phone" wide>
                            <input name="phone" className="form-control" placeholder="(305) 555-0100" />
                        </Field>
                        <Field label="Contract ends">
                            <input type="date" name="contractEnd" className="form-control" />
                        </Field>
                        <Field label="COI expires">
                            <input type="date" name="coi" className="form-control" />
                        </Field>
                        <button type="submit" className="btn btn-primary">Add vendor</button>
                    </div>
                </Panel>

                <div className="cards">
                    {vendors.map((v, i) => {
                        const chip = coiChip(v);
                        return (
                            <Panel key={v.id} index={i}>
                                <img className="panel__media" src={photoOf(v)} alt={`${v.name}, ${v.service.toLowerCase()}`} loading="lazy" />
                                <div className="row__end" style={{ justifyContent: 'space-between' }}>
                                    <Chip tone="muted">{v.service}</Chip>
                                    <Chip tone={chip.tone}>{chip.text}</Chip>
                                </div>
                                <h4>{v.name}</h4>
                                <p className="row__meta">
                                    {v.contact}{v.phone ? ` · ${v.phone}` : ''}
                                </p>
                                <p className="row__meta">
                                    Contract ends {day(v.contractEnd)} · COI {v.coi < 0 ? 'expired' : 'expires'} {day(v.coi)}
                                </p>
                                <p className="row__meta">{v.rating ? `Rated ${v.rating.toFixed(1)} / 5 by staff` : 'Not rated yet'}</p>
                                {!v.requested && (
                                    <button type="button" className="btn btn-outline-light btn-sm" onClick={() => requestCoi(v)}>
                                        Request COI
                                    </button>
                                )}
                            </Panel>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default VendorsScreen;
